import { DigitalResourceResponseDto } from 'client/widget-components/ecom/types/EcomSDK';
import React from 'react';
import styled from 'styled-components';
import { DATA_GRABS } from '../design';
import { formatBytes } from '../productCustomization';
import { SdkErrorNotice, sdkErrorToNotice } from '../sdkErrorNotice';
import { COLORS, OrderViewLinkButton, sp } from '../styles';
import { useEcommL10n, useEcommStr } from '../useEcommStr';
import { downloadFromUrl } from '../utils';
import AccentNotice from './AccentNotice';
import FileFormatIcon from './icons/FileFormatIcon';
import Spinner from './Spinner';
import ThemedText from './ThemedText';

/**
 * React port of snipcart-client's digital-goods downloads, used in the order
 * confirmation widget. Mirrors:
 *  - `components/lineItem/downloads/LineItemDownloads.vue`
 *  - `components/lineItem/downloads/LineItemDownload.vue`
 *
 * One card per purchased resource. The download link is resolved on click —
 * the SDK returns a short-lived URL that redirects to a presigned S3 URL — so
 * each card carries its own pending state while the whole list shares one
 * error notice.
 */

/* -------------------------------------------------------------------------- */
/*  Styled primitives                                                         */
/* -------------------------------------------------------------------------- */

const ListWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sp(1)};
`;

// Adjacent cards read as one stack: the shared edges drop their border and the
// radii survive only on the outer corners.
const List = styled.div`
    display: flex;
    flex-direction: column;

    & > *:not(:last-child) {
        border-block-end: 0;
        border-end-start-radius: 0;
        border-end-end-radius: 0;
    }

    & > *:not(:first-child) {
        border-start-start-radius: 0;
        border-start-end-radius: 0;
    }
`;

const Card = styled.div.attrs({
    'data-grab': DATA_GRABS.dividers,
})`
    display: flex;
    align-items: flex-start;
    gap: ${sp(1.5)};
    position: relative;
    inline-size: 100%;
    padding: ${sp(2)};
    border: 1px solid ${COLORS.border};
    border-radius: 8px;
`;

const LoadingOverlay = styled.div<{ $isLoading: boolean }>`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: ${sp(3)};
    pointer-events: none;
    transition: opacity 200ms;
    opacity: ${({ $isLoading }) => ($isLoading ? 1 : 0)};
`;

const FormatIconSlot = styled.div<{ $isLoading: boolean }>`
    block-size: 1lh;
    inline-size: ${sp(6)};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${COLORS.icon};
    transition: opacity 200ms;
    opacity: ${({ $isLoading }) => ($isLoading ? 0.5 : 1)};
`;

const Content = styled.div<{ $isLoading: boolean }>`
    flex-grow: 1;
    min-inline-size: 0;
    transition: opacity 200ms;
    opacity: ${({ $isLoading }) => ($isLoading ? 0.5 : 1)};
`;

const Title = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
})`
    overflow-wrap: break-word;
`;

const Size = styled(ThemedText).attrs({
    textRole: 'secondaryText' as const,
})``;

/* -------------------------------------------------------------------------- */
/*  LineItemDownload                                                          */
/* -------------------------------------------------------------------------- */

interface LineItemDownloadProps {
    orderId: string;
    lineItemId: string;
    resource: DigitalResourceResponseDto;
    /** False once the order is cancelled or fully refunded. */
    canDownload: boolean;
    onDownloadStart: () => void;
    onError: (error: unknown) => void;
}

const LineItemDownload: React.FC<LineItemDownloadProps> = ({
    orderId,
    lineItemId,
    resource,
    canDownload,
    onDownloadStart,
    onError,
}) => {
    const str = useEcommStr();
    const [isPreparing, setIsPreparing] = React.useState(false);

    const fileSize = formatBytes(resource.file_size);
    const downloadLabel = `${str(
        'nativeEcommRuntime.order.downloads.download_label'
    )} (${fileSize})`;

    const handleDownload = async () => {
        if (!canDownload || isPreparing) return;

        setIsPreparing(true);
        onDownloadStart();

        try {
            const link = await window._ecomm!.getDigitalResourceDownloadLink({
                order_id: orderId,
                line_item_id: lineItemId,
                resource_reference: resource.reference,
            });
            downloadFromUrl(link.url, resource.title);
        } catch (error) {
            onError(error);
        } finally {
            setIsPreparing(false);
        }
    };

    return (
        <Card data-auto='order-item-download'>
            <LoadingOverlay $isLoading={isPreparing}>
                <Spinner />
            </LoadingOverlay>
            <FormatIconSlot $isLoading={isPreparing}>
                <FileFormatIcon mimetype={resource.media_type} />
            </FormatIconSlot>
            <Content $isLoading={isPreparing}>
                <Title>{resource.title}</Title>
                {canDownload ? (
                    <OrderViewLinkButton
                        type='button'
                        disabled={isPreparing}
                        data-auto='order-item-download-link'
                        onClick={() => void handleDownload()}
                    >
                        {downloadLabel}
                    </OrderViewLinkButton>
                ) : (
                    <Size data-auto='order-item-download-size'>{fileSize}</Size>
                )}
            </Content>
        </Card>
    );
};

/* -------------------------------------------------------------------------- */
/*  LineItemDownloads                                                         */
/* -------------------------------------------------------------------------- */

interface LineItemDownloadsProps {
    orderId: string;
    lineItemId: string;
    resources: DigitalResourceResponseDto[];
    /** False once the order is cancelled or fully refunded. */
    canDownload: boolean;
}

const LineItemDownloads: React.FC<LineItemDownloadsProps> = ({
    orderId,
    lineItemId,
    resources,
    canDownload,
}) => {
    const str = useEcommStr();
    const l10n = useEcommL10n();
    const [errorNotice, setErrorNotice] = React.useState<SdkErrorNotice | null>(
        null
    );

    return (
        <ListWrapper data-auto='order-item-downloads'>
            {!canDownload && (
                <AccentNotice
                    level='warning'
                    title={str(
                        'nativeEcommRuntime.order.downloads.unavailable.title'
                    )}
                    description={str(
                        'nativeEcommRuntime.order.downloads.unavailable.description'
                    )}
                    dataAuto='order-downloads-unavailable'
                />
            )}
            {errorNotice && (
                <AccentNotice
                    level='error'
                    title={errorNotice.title}
                    description={errorNotice.description}
                    dataAuto='order-downloads-error'
                />
            )}
            <List>
                {resources.map((resource) => (
                    <LineItemDownload
                        key={resource.reference}
                        orderId={orderId}
                        lineItemId={lineItemId}
                        resource={resource}
                        canDownload={canDownload}
                        onDownloadStart={() => setErrorNotice(null)}
                        onError={(error) =>
                            setErrorNotice(sdkErrorToNotice(error, l10n, str))
                        }
                    />
                ))}
            </List>
        </ListWrapper>
    );
};

export default LineItemDownloads;
